import { Moment } from 'moment'

import { DefaultFile } from 'Core/interfaces'

import { Miscellaneous } from './Miscellaneous'

export enum TermsAndConditionsLabels {
	IsPricingIncluded = 'isPricingIncluded',
	MsaNumber = 'msaNumber',
	EndContractDate = 'endContractDate',
	BeginContractDate = 'beginContractDate',
	MsaDocument = 'msaDocument',
	MsaFileName = 'msaFileName',
	ExpiryContactId = 'expiryContactId',
	ExpiryNotes = 'expiryNotes',
	ExpiryNotificationDate = 'expiryNotificationDate'
}

export enum SupplierDocumentType {
	MSA = 'MSA',
	TermsAndConditions = 'Terms & Conditions',
	NDA = 'NDA',
	CertificateOfInsurance = 'Certificate of Insurance',
	ProofOfInsurance = 'Proof of Insurance'
}

export declare namespace Supplier {
	interface Contact extends Entity {
		company: Entity[] | null
		isActive: boolean
	}
	interface Basic extends Entity {
		isActive: boolean
		termsNeeded: boolean
		termsExecuted: boolean
	}

	interface Detailed extends Entity {
		company: Entity
		billingAgencies: Entity[]
		comments: string | null
		isActive: boolean
		termsAndConditionsNeeded: boolean
		termsAndConditionsExecuted: boolean
		supplierLocationClassCode: string | null
		lastSiteVisitDate: string
		isTurkish: boolean
	}

	interface Request {
		name: string
		companyId: Key
		billingAgencyIds: number[]
		isActive: boolean
		termsAndConditionsNeeded: boolean
		termsAndConditionsExecuted: boolean
		comments: string | null
		lastSiteVisitDate: string | null
	}

	namespace Form {
		interface Add
			extends Omit<Request, 'billingAgencyIds' | 'lastSiteVisitDate' | 'companyId'> {
			companyId: LabelValue | null
			billingAgencies: LabelValue<number>[]
			lastSiteVisitDate: Moment | null
			supplierLocationClassCode: BasicString
		}

		interface Edit extends Form.Add {
			supplierLocationClassCode: string | null
		}
	}

	type Form = Form.Add | Form.Edit

	namespace TermsConditions {
		interface Basic {
			id: number
			endDate: string | null
			isPricingIncluded: boolean
			enteredBy: string
			msaNumber: string
			msaFileName: string
			startDate: string | null
			expiryContact: Entity | null
			expiryNotes: BasicString
			expiryNotificationDate: BasicString
		}

		interface Form
			extends Pick<
				TermsConditions.Basic,
				| TermsAndConditionsLabels.IsPricingIncluded
				| TermsAndConditionsLabels.MsaNumber
				| TermsAndConditionsLabels.ExpiryNotes
			> {
			[TermsAndConditionsLabels.EndContractDate]: Moment | null
			[TermsAndConditionsLabels.BeginContractDate]: Moment | null
			[TermsAndConditionsLabels.ExpiryNotificationDate]: Moment | null
			[TermsAndConditionsLabels.ExpiryContactId]: number | null
			[TermsAndConditionsLabels.MsaDocument]: DefaultFile | null
		}

		interface Request
			extends Pick<
				TermsConditions.Basic,
				| TermsAndConditionsLabels.IsPricingIncluded
				| TermsAndConditionsLabels.MsaNumber
				| TermsAndConditionsLabels.ExpiryNotes
			> {
			[TermsAndConditionsLabels.MsaDocument]: File | null
			[TermsAndConditionsLabels.EndContractDate]: string | null
			[TermsAndConditionsLabels.BeginContractDate]: BasicString
			[TermsAndConditionsLabels.ExpiryNotificationDate]: BasicString
			[TermsAndConditionsLabels.ExpiryContactId]: BasicNumber
		}
	}

	interface Location extends Entity {
		city: string
		state: Miscellaneous.StateProvince
		isActive: boolean
		qualityRating: number
	}

	namespace Report {
		interface Basic {
			id: number
			reportId: Key
			reportName: string | null
			createDate: string
		}

		interface Form {
			reportId: Key | null
		}
	}

	namespace Document {
		interface Basic {
			id: number
			documentName: BasicString
			documentType: Entity
			documentStatus: BasicString
			expirationDate: BasicString
			lastUpdatedBy: BasicString
			lastUpdateDate: BasicString
			comment: BasicString
			hasPendingStatus: boolean
		}

		namespace Form {
			interface Add {
				documentTypeId: number
				expirationDate: BasicString
				file: DefaultFile | null
			}
		}

		namespace Request {
			interface Add {
				documentTypeId: number
				expirationDate: BasicString
				file: File | null
			}
		}
	}

	namespace DocumentType {
		interface Basic {
			id: number
			documentName: BasicString
			documentType: Entity | null
			documentStatus: BasicString
			expirationDate: BasicString
			lastUpdatedBy: BasicString
			lastUpdateDate: BasicString
			comment: BasicString
		}

		namespace Form {
			interface Edit {
				expirationDate: moment.Moment | null
				comment: BasicString
			}
		}

		namespace Request {
			interface Edit {
				expirationDate: BasicString
				comment: BasicString
				documentStatus: number
			}
		}
	}
}
